import Button from "components/Button";
import InputField from "components/InputField";
import StepIndicator from "components/StepIndicator";
import { InternalBookingAuthValues } from "entities/Form";
import { ChangeEvent, FC, PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./InternalValidation.module.scss";

interface InternalValidationProps {
  onSubmit: (
    authValues: InternalBookingAuthValues,
    onInvalid: () => void,
    onError: () => void
  ) => void;
  onBack: () => void;
}

const defaultValues: InternalBookingAuthValues = {
  accountName: "",
  password: "",
};

const InternalValidation: FC<PropsWithChildren<InternalValidationProps>> = ({
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const [values, setValues] =
    useState<InternalBookingAuthValues>(defaultValues);
  const [errors, setErrors] = useState<
    Partial<Record<keyof InternalBookingAuthValues, string>>
  >({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isAllRequiredFilled = !!values.accountName && !!values.password;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (submitted) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateAllFields = () => {
    const nextErrors = {
      accountName: values.accountName ? undefined : t("accountNameRequired"),
      password: values.password ? undefined : t("passwordRequired"),
    };

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (!validateAllFields()) return;

    setSubmitting(true);
    onSubmit(
      values,
      () => {
        setErrors((prev) => ({
          ...prev,
          password: t("passwordError"),
        }));
        setSubmitting(false);
      },
      () => setSubmitting(false)
    );
  };

  return (
    <div className={styles.container}>
      <StepIndicator value={3} total={3} />
      <span className={styles.pageTitle}>{t("employeeAuthentication")}</span>
      <div className={styles.content}>
        <div className={styles.title}>
          {t("employeeAuthenticationInstruction")}
        </div>
        <div className={styles.fields}>
          <InputField
            name="accountName"
            value={values.accountName}
            onChange={handleChange}
            title={t("accountName")}
            placeholder={t("enterAccountName")}
            error={errors.accountName}
          />
          <InputField
            type="password"
            name="password"
            value={values.password}
            onChange={handleChange}
            title={t("password")}
            placeholder={t("enterPassword")}
            error={errors.password}
          />
        </div>
        <div className={styles.buttons}>
          <Button
            type="primary"
            disabled={!isAllRequiredFilled}
            loading={submitting}
            onClick={handleSubmit}
          >
            <span>{t("login")}</span>
          </Button>
          <Button type="secondary" onClick={onBack} disabled={submitting}>
            <span>{t("returnToEdit")}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InternalValidation;
